/** Bundled UI strings (src/locales) plus the backend's country; product names and prices stay the website's. */

import { fetchApiJson } from "../core/ApiData.js";
import en from "../locales/en.json";

// data-* key -> the attribute it translates into.
const I18N_ATTRIBUTES = [
  ['data-i18n-title', 'title'],
  ['data-i18n-aria-label', 'aria-label'],
];

export const SOURCE_LANGUAGE = 'en';

// English is the eager per-key fallback; every other language is a lazy chunk.
const LOCALE_LOADERS = Object.fromEntries(
  Object.entries(import.meta.glob(['../locales/*.json', '!../locales/en.json'], { import: 'default' }))
    .map(([path, load]) => [path.match(/([\w-]+)\.json$/)[1], load]),
);

export const SUPPORTED_LANGUAGES = [SOURCE_LANGUAGE, ...Object.keys(LOCALE_LOADERS)];

/** The first candidate a locale ships for, by exact tag and then by base language (`de-AT` → `de`). */
export function resolveLanguage(candidates) {
  for (const candidate of candidates) {
    if (!candidate) continue;
    const tag = String(candidate).toLowerCase().replace(/_/g, '-');
    if (SUPPORTED_LANGUAGES.includes(tag)) return tag;
    const base = tag.split('-')[0];
    if (SUPPORTED_LANGUAGES.includes(base)) return base;
  }
  return SOURCE_LANGUAGE;
}

export class LocalizationManager {
  constructor(app) {
    this.app = app;
    this.translations = en;
    this.localTranslations = null;
    this.language = SOURCE_LANGUAGE;
    this.countryLanguage = null;
    this.country = 'GLOBAL';
    this.isInitialized = false;
    this.usingEnglish = false;
  }

  async init() {
    await this.fetchCountryInfo();

    // Country language first, matching the website and its product names; offline falls to the device's.
    await this.loadLanguage(resolveLanguage([
      this.getCountryOverride()?.lang,
      this.countryLanguage,
      ...this.getBrowserLanguages(),
    ]));

    this.applyTranslationsToDOM();

    this.isInitialized = true;
  }

  /**
   * Get country override from URL parameter (for development/testing)
   */
  getCountryOverride() {
    const urlParams = new URLSearchParams(window.location.search);
    const country = urlParams.get('country');
    const lang = urlParams.get('lang');

    if (country || lang) {
      return { country, lang };
    }
    return null;
  }

  /**
   * Fetch country info from backend API
   */
  async fetchCountryInfo() {
    // No shipped snapshot: the answer is derived from the caller's IP, so a default one
    // would be a different country's. The device cache is what carries it offline — a
    // second launch keeps the country the first one resolved.
    const override = this.getCountryOverride();
    const data = await fetchApiJson('country-info', {
      params: override ? { country: override.country, lang: override.lang } : null,
    });

    if (!data) {
      console.warn('No country info available, using the device language');
      this.country = 'GLOBAL';
      return;
    }

    this.country = data.countryCode || 'GLOBAL';
    this.countryLanguage = data.language || null;
  }

  getBrowserLanguages() {
    if (typeof navigator === 'undefined') return [];
    return navigator.languages?.length ? navigator.languages : [navigator.language];
  }

  /** Load a bundled locale; keys it lacks fall back to English one by one. */
  async loadLanguage(language) {
    this.language = language;
    if (language === SOURCE_LANGUAGE) return;

    try {
      const strings = await LOCALE_LOADERS[language]();
      this.localTranslations = { ...en, ...strings };
      this.translations = this.localTranslations;
    } catch (error) {
      // A chunk can fail on the web after a deploy replaced it under a cached index.html.
      console.warn(`Could not load the "${language}" locale, using English`, error);
      this.language = SOURCE_LANGUAGE;
    }
  }

  /**
   * Get translation for a key
   * @param {string} key - Translation key
   * @param {string} fallback - Fallback text if key not found
   * @param {Object} params - Parameters for interpolation (e.g., {count: 5})
   * @returns {string} Translated string
   */
  t(key, fallback = '', params = null) {
    let text = this.translations[key];

    if (text === undefined || text === '') {
      text = fallback || key;
    }

    // Handle interpolation (e.g., "{count} items selected")
    if (params && typeof text === 'string') {
      text = this.formatString(text, params);
    }

    return text;
  }

  /**
   * Format string with parameters
   * Replaces {paramName} with param values
   * @param {string} template - String template
   * @param {Object} params - Key-value pairs for substitution
   * @returns {string} Formatted string
   */
  formatString(template, params) {
    return template.replace(/\{(\w+)\}/g, (match, key) => {
      return params.hasOwnProperty(key) ? params[key] : match;
    });
  }

  /** Apply translations to all DOM elements with data-i18n attribute */
  applyTranslationsToDOM() {
    // Attribute-only keys are selected too: an icon button carries no data-i18n.
    const elements = document.querySelectorAll('[data-i18n], [data-i18n-title], [data-i18n-aria-label]');

    elements.forEach(element => {
      const key = element.getAttribute('data-i18n');
      const translation = key ? this.t(key) : null;

      if (translation && translation !== key) {
        // Check if it's an input element
        if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
          if (element.placeholder) {
            element.placeholder = translation;
          }
        } else if (element.tagName === 'OPTION') {
          element.textContent = translation;
        } else {
          element.textContent = translation;
        }
      }

      // Tooltip and accessible name — the latter is all an icon-only control announces.
      for (const [dataAttr, attr] of I18N_ATTRIBUTES) {
        const attrKey = element.getAttribute(dataAttr);
        if (!attrKey) continue;
        const attrTranslation = this.t(attrKey);
        if (attrTranslation && attrTranslation !== attrKey) element.setAttribute(attr, attrTranslation);
      }
    });

    // Reveal elements hidden until the language was known (e.g. the splash heading) so the first visible text is already localized.
    document.querySelectorAll('.i18n-pending').forEach(el => {
      el.classList.remove('i18n-pending');
    });
  }

  /**
   * Re-apply translations (useful after dynamic content is added)
   */
  refresh() {
    this.applyTranslationsToDOM();
  }

  /**
   * Get current language code
   */
  getLanguage() {
    return this.language;
  }

  /**
   * Get current country code
   */
  getCountry() {
    return this.country;
  }

  /**
   * Whether local (non-English) translations are available
   */
  hasLocalTranslations() {
    return this.localTranslations !== null;
  }

  switchToLocal() {
    if (!this.localTranslations) return;
    this.usingEnglish = false;
    this.translations = this.localTranslations;
    this.applyTranslationsToDOM();
  }

  switchToEnglish() {
    this.usingEnglish = true;
    this.translations = en;
    this.applyTranslationsToDOM();
  }
}
